import { Grid, Typography } from '@mui/material';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import { RunSlideRegistrationsFormValues } from 'components/StudyDashboard/ProceduresPage/Actions/RunSlideRegistrations/types';
import { filter, includes, keys, map, uniq } from 'lodash';
import { default as React, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { SlideMetadata } from './SlideMetadata';

export const metadataOptions = {
  negativeControl: {
    value: 'negative_control',
    text: 'Negative Control',
    sourceFieldName: 'sourceNegativeControl',
    targetFieldName: 'targetNegativeControl',
    options: [
      { value: false, text: 'No' },
      { value: true, text: 'Yes' },
    ] as DropdownOption[],
  },
} as const;

export type MetadataOptionKey = keyof typeof metadataOptions;

export interface SelectSlidesStepProps {
  methods: UseFormReturn<RunSlideRegistrationsFormValues, any>;
  checkValidationAndSetIsStepFailed: () => void;
  slides?: any[];
  loading: boolean;
  createSlideManifest: () => void;
  sourceSelectedMetadataOptions: MetadataOptionKey[];
  setSourceSelectedMetadataOptions: React.Dispatch<React.SetStateAction<MetadataOptionKey[]>>;
  targetSelectedMetadataOptions: MetadataOptionKey[];
  setTargetSelectedMetadataOptions: React.Dispatch<React.SetStateAction<MetadataOptionKey[]>>;
}

export const SelectSlidesStep: React.FC<SelectSlidesStepProps> = ({
  methods,
  checkValidationAndSetIsStepFailed,
  slides,
  loading,
  sourceSelectedMetadataOptions,
  setSourceSelectedMetadataOptions,
  targetSelectedMetadataOptions,
  setTargetSelectedMetadataOptions,
  createSlideManifest,
}) => {
  const {
    control,
    formState: { errors },
  } = methods;

  const removeSourceMetadataOption = (optionKey: MetadataOptionKey) => {
    setSourceSelectedMetadataOptions((prev) => filter(prev, (key) => key !== optionKey));
    methods.setValue(metadataOptions[optionKey].sourceFieldName as any, undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });
    checkValidationAndSetIsStepFailed();
  };

  const removeTargetMetadataOption = (optionKey: MetadataOptionKey) => {
    setTargetSelectedMetadataOptions((prev) => filter(prev, (key) => key !== optionKey));
    methods.setValue(metadataOptions[optionKey].targetFieldName as any, undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });
    checkValidationAndSetIsStepFailed();
  };

  const stainTypesInStudy = uniq(map(slides, (slide) => slide.stainingType));
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const stainTypeDropDownOptions: DropdownOption[] = map(stainTypesInStudy, (currStain) => ({
    value: currStain,
    text: stainTypeIdToDisplayName(currStain),
  }));

  const { availableSourceMetadataOptionKeys, availableTargetMetadataOptionKeys } = useMemo(() => {
    const allMetadataOptionKeys = keys(metadataOptions) as MetadataOptionKey[];
    const availableSource = filter(
      allMetadataOptionKeys,
      (optionKey) => !includes(sourceSelectedMetadataOptions, optionKey)
    );
    const availableTarget = filter(
      allMetadataOptionKeys,
      (optionKey) => !includes(targetSelectedMetadataOptions, optionKey)
    );
    return { availableSourceMetadataOptionKeys: availableSource, availableTargetMetadataOptionKeys: availableTarget };
  }, [sourceSelectedMetadataOptions, targetSelectedMetadataOptions]);

  return (
    <Grid container direction="column" spacing={2}>
      <SlideMetadata
        key="source-metadata"
        id="source-metadata"
        title="Source slide metadata:"
        metadataOptionFieldName="sourceFieldName"
        stainFieldName="sourceStain"
        stainOptions={stainTypeDropDownOptions}
        control={control}
        errors={errors}
        metadataOptions={metadataOptions}
        selectedMetadataOptions={sourceSelectedMetadataOptions}
        setSelectedMetadataOptions={setSourceSelectedMetadataOptions}
        availableMetadataOptionKeys={availableSourceMetadataOptionKeys}
        loading={loading}
        onRemoveMetadataOption={removeSourceMetadataOption}
        onValidate={checkValidationAndSetIsStepFailed}
        createSlideManifest={createSlideManifest}
      />

      <SlideMetadata
        key="target-metadata"
        id="target-metadata"
        title="Target slide metadata:"
        metadataOptionFieldName="targetFieldName"
        stainFieldName="targetStain"
        stainOptions={stainTypeDropDownOptions}
        control={control}
        errors={errors}
        metadataOptions={metadataOptions}
        selectedMetadataOptions={targetSelectedMetadataOptions}
        setSelectedMetadataOptions={setTargetSelectedMetadataOptions}
        availableMetadataOptionKeys={availableTargetMetadataOptionKeys}
        loading={loading}
        onRemoveMetadataOption={removeTargetMetadataOption}
        onValidate={checkValidationAndSetIsStepFailed}
        createSlideManifest={createSlideManifest}
      />
      <Grid item>
        <Typography variant="body2" color="error">
          {errors.slideManifest ? errors.slideManifest.message : ''}
        </Typography>
      </Grid>
    </Grid>
  );
};
